import React from "react";
import { PillGroup, Pill } from "./ui/Pill";
import { Num } from "./ui/Num";
import { RecipientsGrid } from "./RecipientsGrid";
import { RECIPIENT_STATES } from "./broadcastEnums";

/**
 * The Recipients tab: who this broadcast reached, and who it did not.
 *
 * ---------------------------------------------------------------------------
 * PRESENTATIONAL ONLY
 * ---------------------------------------------------------------------------
 * This component fetches nothing. `recipients`, `loading`, `error` and `total`
 * all arrive as props from the detail page, which owns the request. That split
 * is what lets the tab be rendered in a test with four lines of fixture, and it
 * is what stops a tab switch from becoming a second, competing owner of the
 * same request.
 *
 * DEBOUNCING IS THE PAGE'S JOB. The search box below is a plain controlled
 * input: every keystroke calls `onQueryChange` immediately. It does not hold a
 * timer, because a debounce inside the input would fight the page's own
 * scheduling and would make the field lag its own value. The page decides how
 * long to wait before turning a query into a request.
 */

const INPUT_CLASS =
  "w-full rounded-lg border border-bc-border bg-white px-3 py-1.5 text-[13px] text-bc-text placeholder:text-bc-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary";

/**
 * The Recipients tab.
 *
 * @param {Object} props
 * @param {Array<Object>} props.recipients - Decoded recipient rows, one per person.
 * @param {boolean} [props.loading=false] - A fetch is in flight; the grid paints skeletons.
 * @param {string|null} [props.error] - A user-facing error message, or null.
 * @param {string} [props.filter] - Selected delivery-state id, one of RECIPIENT_STATES.
 * @param {(state: string) => void} [props.onFilterChange] - Called with the newly selected state id.
 * @param {string} [props.query] - The current search text.
 * @param {(query: string) => void} [props.onQueryChange] - Called with the raw input value on every keystroke.
 * @param {number|null} [props.total] - Server's total for the CURRENT filter. `null` renders an em dash.
 * @returns {React.ReactElement}
 */
export const RecipientsTab = ({
  recipients,
  loading = false,
  error = null,
  filter,
  onFilterChange,
  query = "",
  onQueryChange,
  total = null,
}) => (
  <section className="flex flex-col gap-3">
    <div className="flex flex-wrap items-center gap-3">
      <PillGroup label="Delivery state">
        {RECIPIENT_STATES.map((state) => (
          <Pill
            key={state.id}
            selected={state.id === filter}
            onSelect={() => onFilterChange?.(state.id)}
          >
            {state.label}
          </Pill>
        ))}
      </PillGroup>

      {/* `ml-auto` rather than `justify-between`: when the row wraps the search
          box drops to its own line and stays right-aligned instead of the two
          groups springing apart mid-row. */}
      <div className="ml-auto w-full max-w-[240px]">
        <input
          type="search"
          aria-label="Search recipients"
          value={query}
          onChange={(event) => onQueryChange?.(event.target.value)}
          placeholder="Name, email or phone"
          className={INPUT_CLASS}
        />
      </div>
    </div>

    {/* `Num` so an unknown total is an em dash rather than a confident `0`. */}
    <p className="text-xs text-bc-soft">
      <Num value={total} /> recipients
    </p>

    {error ? (
      <p role="alert" className="text-xs text-bc-bad">
        {error}
      </p>
    ) : null}

    <RecipientsGrid
      recipients={recipients}
      loading={loading}
      lastColumn="state"
      emptyMessage="No recipients in this state."
    />
  </section>
);

export default RecipientsTab;
